import { Nullable } from '@types'

import { Company, Delegate, ProfileType } from '@modules/search/types'

import { Chat, ChatMember, ChatMessage, ChatType, GetChatsResponse, SortChatType } from '../types'
import { getApiClient } from './apiClient'

export const fetchChatWhere = async ({
  delegateIds,
  companyId,
}: {
  delegateIds: Delegate['id'][]
  companyId?: Company['id']
}): Promise<Nullable<Chat>> => {
  const response = await getApiClient()
    .post('/chat/find', JSON.stringify({ delegateIds, companyId }))
    // It is temp because now BE throws an error if there is no chat, will be fixed soon
    .catch(() => ({ data: null }))

  return response.data?.chat
}

export const fetchChatById = async ({ chatId }: { chatId: Chat['id'] }): Promise<Nullable<Chat>> => {
  const response = await getApiClient().get(`/chat/${chatId}`)

  return response.data?.chat
}

export const createChat = async ({
  message,
  toDelegateIds,
  toCompanyId,
  chatType,
}: {
  message: string
  toDelegateIds: Delegate['id'][]
  toCompanyId: Company['id']
  chatType: ChatType
}): Promise<Chat> => {
  const response = await getApiClient().post(
    '/chat/initiate',
    JSON.stringify({ content: message, toUserIds: toDelegateIds, toCompanyId, chatType }),
  )

  return response.data?.chat
}

export const fetchChats = async ({
  pageParam,
  size = 10,
  sortBy = SortChatType.updated,
}: {
  pageParam?: number
  size?: number
  sortBy?: SortChatType
} = {}): Promise<GetChatsResponse> => {
  const from = pageParam ? pageParam * size : 0

  const response = await getApiClient().get(`/chat/?size=${size || 10}&from=${from}&sortBy=${sortBy}`)

  return response.data
}

export const fetchChatMessages = async ({
  chatId,
  pageParam,
  size = 10,
}: {
  chatId: Chat['id']
  pageParam?: number
  size?: number
}): Promise<{ messages: ChatMessage[]; members: ChatMember[]; total: number }> => {
  const from = pageParam ? pageParam * size : 0

  const response = await getApiClient().get(`/chat/${chatId}/message?size=${size}&from=${from}`)

  return response.data
}

export const fetchUnreadCount = async (): Promise<{
  counts: { unread: { total: number; byChatId: Record<Chat['id'], number> } }
}> => {
  const response = await getApiClient().get('/chat/counts')

  return response.data
}

export const sendChatMessage = async ({ chatId, content }: { chatId: Chat['id']; content: string }): Promise<void> => {
  const response = await getApiClient().post(`/chat/${chatId}/message`, JSON.stringify({ content }))

  return response.data
}

export const markChatAsUnread = async ({ chatId }: { chatId: Chat['id'] }): Promise<void> => {
  return getApiClient().post(`/chat/${chatId}/unread`)
}

export const checkContactsWith = async ({
  delegateIds,
  companyIds,
}: {
  delegateIds?: number[]
  companyIds?: number[]
}): Promise<
  Nullable<{
    [ProfileType.company]: Record<string, boolean>
    [ProfileType.delegate]: Record<string, boolean>
  }>
> => {
  if (!delegateIds?.length && !companyIds?.length) {
    return null
  }

  const response = await getApiClient().post(
    '/chat/check-contacts',
    JSON.stringify({ delegateIds: [...Array.from(new Set(delegateIds))], companyIds: [...Array.from(new Set(companyIds))] }),
  )

  return response.data?.contactedWith
}

export const checkProfileAccess = async ({
  profileType,
  profileId,
}: {
  profileType?: ProfileType
  profileId?: string
}): Promise<Nullable<{ isAccessible: boolean }>> => {
  if (!profileType && !profileId) {
    return null
  }

  const response = await getApiClient().post(
    '/chat/check-profile-access',
    JSON.stringify({
      profileType,
      profileId,
    }),
  )

  return response.data
}
